<template>
  <v-dialog
    v-model="model"
    v-bind="$attrs"
    :max-width="maxWidth"
    :class="contentClasses"
    :persistent="isPersistent"
    attach="#approot"
  >
    <v-card>
      <Button
        v-if="showCloseButton"
        class="close-btn"
        :icon-btn="true"
        color="text-black"
        variant="plain"
        @click="$emit('cancel'); close()"
      >
        <v-icon>mdi-close</v-icon>
      </Button>

      <v-card-title class="text-h3">
        <v-icon
          v-if="showIcon"
          class="error-icon"
          color="error"
          size="small"
        >
          {{ icon }}
        </v-icon>
        {{ title }}
      </v-card-title>

      <v-card-text>
        <slot name="content"/>
      </v-card-text>

      <v-card-actions v-if="showActions">
        <slot name="actions">
          <div class="d-flex w-100 justify-content-around">
            <Button
              color="error"
              class="col-2 pl-6 pr-6 mr-2 ellipsis"
              :disabled="saveInProgress || disabled"
              :label="$t(props.cancelText)"
              @click="$emit('cancel'); close()"
            />

            <Button
              class="col-2 pl-6 pr-6 ml-2"
              :disabled="Boolean(saveInProgress) || Boolean(disabled)"
              @click="$emit('save', saveArgs)"
            >
              {{ $t("Save") }}
              <v-progress-circular
                v-if="saveInProgress"
                class="ml-2"
                right
                indeterminate
                color="white"
                size="20"
                width="2"
              />
            </Button>

          </div>
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import {computed, useAttrs} from "vue";
import Button from "@/components/input-components/Button.vue";

const props = defineProps({
  modelValue: {type: Boolean, required: true},
  maxWidth: {type: String, default: "525px"},
  title: {type: String, default: ""},
  icon: {type: String, default: "mdi-alert-circle"},
  showIcon: {type: Boolean, default: true},
  showActions: {type: Boolean, default: true},
  showCloseButton: {type: Boolean, default: true},
  saveInProgress: {type: Boolean, default: false},
  errorOnSave: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
  persistent: {type: Boolean, default: false},
  saveArgs: {type: Object, default: () => ({})},
  cancelText:  {type: String, default: 'Discard Changes and Exit'},
});

const emit = defineEmits(["update:modelValue", "save", "cancel"]);
const model = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit("update:modelValue", val),
});

function close() {
  model.value = false;
}

const attrs = useAttrs();

const contentClasses = computed(() => {
  const cls = ["dialog"]
  const extra = attrs["content-class"]
  if (typeof extra === "string" && extra.trim() !== "") {
    cls.push(extra)
  }
  return cls.join(" ")
})

const isPersistent = computed(() => {
  const a = attrs.persistent
  const persistentAttr =
    a === true || a === "true" || a === ""
  return !!props.saveInProgress || !!props.persistent || persistentAttr
})
</script>
<style lang="scss" scoped>
.v-dialog {
  :deep(.v-card) {
    padding: 2rem 3.75rem;
    background-color: white;
    color: rgb(var(--v-theme-black)) !important;

    .v-card-title,
    .v-card-text,
    .v-card-actions {
      padding: 0;
    }

    .v-card-title,
    .v-card-text {
      padding-bottom: 2.1875rem;
    }

    .v-card-actions {
      flex-wrap: wrap;

      button {
        flex: 1;

        @media #{map.get($display-breakpoints, 'xs-only')} {
          flex: 0 0 100%;

          &.v-btn + .v-btn {
            margin-top: 0.625rem;
            margin-left: 0;
          }
        }
      }

      .error-container {
        flex: 0 0 100%;
        margin-top: 0.625rem;
      }
    }

    .v-card-text {
      font-size: 1rem;
    }

    .v-card-title {
      font-weight: 600;
      text-transform: uppercase !important;
      line-height: 1.375rem;
      word-break: normal;
    }

    .close-btn {
      position: absolute;
      top: 0.125rem;
      right: 0.125rem;
    }

    .error-icon {
      position: absolute;
      left: 1.25rem;
    }

    @media #{map.get($display-breakpoints, 'xs-only')} {
      padding: 2rem;

      .error-icon {
        left: 0.25rem;
      }
    }
  }
}
</style>
